import React, { useState } from "react";
import LogoUploadModal from "./LogoUploadModal";
import "../../styles/LogoSelector.css";

interface Logo {
  id: string;
  name: string;
  logoUrl: string;
  isDefault: boolean;
  isDeletable: boolean;
  source: "asset" | "storage";
}

interface LogoSelectorProps {
  selectedLogoId: string;
  onLogoSelect: (logoId: string) => void;
  allLogos: Logo[];
  onLogoDelete: (logoId: string, logoName: string) => Promise<boolean>;
  onLogoUpload: (file: File, name: string) => Promise<boolean>;
  validateLogoFile: (file: File) => string | null;
  validateLogoName: (name: string) => string | null;
}

const LogoSelector: React.FC<LogoSelectorProps> = ({
  selectedLogoId,
  onLogoSelect,
  allLogos,
  onLogoDelete,
  onLogoUpload,
  validateLogoFile,
  validateLogoName,
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const handleDeleteClick = async (
    logoId: string,
    logoName: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const success = await onLogoDelete(logoId, logoName);
    if (success && selectedLogoId === logoId) {
      onLogoSelect("cgi");
    }
  };

  const handleLogoUpload = async (
    file: File,
    name: string
  ): Promise<boolean> => {
    const success = await onLogoUpload(file, name);
    if (success) setShowUploadModal(false);
    return success;
  };

  const handleTileKeyDown = (
    logoId: string,
    e: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onLogoSelect(logoId);
    }
  };

  return (
    <div className="logo-selector">
      <div className="logo-options">
        {allLogos.map((logo) => (
          <div
            key={logo.id}
            className={`logo-option ${
              selectedLogoId === logo.id ? "selected" : ""
            } ${logo.isDefault ? "default" : ""}`}
            onClick={() => onLogoSelect(logo.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => handleTileKeyDown(logo.id, e)}
          >
            {logo.isDeletable && (
              <button
                type="button"
                className="delete-btn"
                onClick={(e) => handleDeleteClick(logo.id, logo.name, e)}
                title={`Delete ${logo.name}`}
                aria-label={`Delete ${logo.name}`}
              >
                <img
                  src="https://brand.cgi.com/images/ico_x_3.svg"
                  alt=""
                  style={{ width: 12, height: 12 }}
                />
              </button>
            )}

            <div className="logo-preview">
              <img
                src={logo.logoUrl}
                alt={logo.name}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className="logo-option add-logo"
          onClick={(e) => {
            e.preventDefault();
            setShowUploadModal(true);
          }}
        >
          <div
            className="logo-preview"
            style={{
              color: "#5236ab",
              fontSize: 24,
              fontWeight: "bold",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <div style={{ fontSize: 34, lineHeight: 1 }}>+</div>
            <div className="logo-name">Add Logo</div>
          </div>
        </button>
      </div>

      {showUploadModal && (
        <LogoUploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleLogoUpload}
          validateFile={validateLogoFile}
          validateName={validateLogoName}
        />
      )}
    </div>
  );
};

export default LogoSelector;
